import { useMutation } from '@tanstack/react-query'
import { httpClient } from '../../shared/api/httpClient'
import { useAuth } from '../../shared/context/AuthContext'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/inactivator/inactivatorController.js` + `app/inactivator/
 * inactivatorService.js` to TanStack Query mutation hooks.
 *
 * Legacy behavior (confirmed by reading the AngularJS source):
 *
 *   $scope.inactiveAccount = function(tenantId) {
 *     $http.post(self.getUrlAstrea() + "/api/v2/devchanger/cleanaccount?tenantId=" + tenantId)
 *       .then(function(resp) { self.saveLog(tenantId); Materialize.toast(...) })
 *       .catch(function(error) { Materialize.toast(error.data.errorMessage, 5000); });
 *   }
 *
 *   $scope.inactiveEmail = function(email) {
 *     $http.post(self.getUrlAstrea() + "/api/v2/devchanger/cleanaemail?email=" + email)
 *       .then(...).catch(...) // same shape as inactiveAccount
 *   }
 *
 *   $scope.changeLogin = function(oldEmail, newEmail) {
 *     $http.post(self.getUrlAstrea() + "/api/v2/users/change-login", { oldLogin: oldEmail, newLogin: newEmail })
 *       .then(...).catch(...) // same shape as inactiveAccount
 *   }
 *
 * All three hit the *external* Astrea backend (`self.getUrlAstrea()` ==
 * `AuthService.getUrlAstreaPath()`), not this repo's own `server_app`
 * (confirmed via `grep -rn "devchanger\|change-login" server_app/` — no
 * hits). Same cross-origin pattern already established by
 * `useLogoutUsersMutation` (`client/src/modules/logoutUsers/
 * useLogoutUsersMutation.js`, task running concurrently): pass the absolute
 * Astrea URL as the request `url` so axios uses it as-is instead of
 * prefixing `httpClient`'s own `baseURL`, while still going through the
 * shared `httpClient` instance so the legacy `HttpInterceptor`'s Basic Auth
 * header (mirrored unconditionally by `httpClient`'s own request
 * interceptor) is still attached — see that file's doc-comment for the full
 * reasoning, not duplicated here.
 *
 * `self.saveLog(data)` / `self.saveLogChangeLogin(oldEmail, newEmail)`
 * (`InactivatorService.saveLog`) is a *local* `server_app` call
 * (`POST /statusLogs/create`, see `server_app/routes/statusLogsRouter.js`),
 * fired-and-forgotten after each successful action (the legacy code only
 * logs a `console.log` on failure, never surfaces it to the user or blocks
 * the success toast) — ported below as a best-effort side effect inside each
 * mutation's `onSuccess`, matching that "never block on it" behavior.
 *
 * `error.data.errorMessage` (legacy `$http` rejection shape) is
 * `error.response.data.errorMessage` under axios; each mutation surfaces
 * that through `mutation.error` so the page component can render the
 * message, since there is no MUI/global toast equivalent of `Materialize.
 * toast` reused elsewhere yet (see `SettingsPage.jsx`'s own local `Snackbar`
 * precedent — this module follows the same "local feedback state" approach).
 */

function saveLog({ getUserLogin, namespace, oldStatus, newStatus }) {
  return httpClient
    .post('/statusLogs/create', {
      who: getUserLogin(),
      namespace,
      oldStatus,
      newStatus,
    })
    .catch(() => {
      // Legacy `.catch(function(error){ console.log(error) })` — logging
      // this failure is not user-facing in the legacy app either, so it is
      // swallowed here too rather than surfacing a second error path for a
      // fire-and-forget audit log.
    })
}

/** Port of `$scope.inactiveAccount(tenantId)`. */
export function useInactivateAccountMutation() {
  const { getUserLogin } = useAuth()
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (tenantId) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/devchanger/cleanaccount?tenantId=${encodeURIComponent(tenantId)}`,
      )
      return response.data
    },
    onSuccess: (_data, tenantId) => {
      saveLog({
        getUserLogin,
        namespace: tenantId,
        oldStatus: `${tenantId} active`,
        newStatus: `${tenantId} inactive`,
      })
    },
  })
}

/** Port of `$scope.inactiveEmail(email)`. */
export function useInactivateEmailMutation() {
  const { getUserLogin } = useAuth()
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (email) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/devchanger/cleanaemail?email=${encodeURIComponent(email)}`,
      )
      return response.data
    },
    onSuccess: (_data, email) => {
      saveLog({
        getUserLogin,
        namespace: email,
        oldStatus: `${email} active`,
        newStatus: `${email} inactive`,
      })
    },
  })
}

/** Port of `$scope.changeLogin(oldEmail, newEmail)`. */
export function useChangeLoginMutation() {
  const { getUserLogin } = useAuth()
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ oldEmail, newEmail }) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/users/change-login`, {
        oldLogin: oldEmail,
        newLogin: newEmail,
      })
      return response.data
    },
    onSuccess: (_data, { oldEmail, newEmail }) => {
      // Port of `self.saveLogChangeLogin` — note the legacy version starts
      // with `console.log(data)` referencing an out-of-scope `data` variable
      // (a copy-paste leftover from `saveLog`, would throw a
      // `ReferenceError` in the legacy code too since `data` isn't a
      // parameter of `saveLogChangeLogin`). Not ported: it's a latent legacy
      // bug that never affects the actual log write (the `try`-less
      // `ReferenceError` would only ever surface as an uncaught console
      // error, not block `InactivatorService.saveLog`... though in practice
      // it *would* throw before reaching that call. Preserving that bug
      // would just silently break this audit log path, which is worse than
      // fixing it, so `saveLog` below is called directly without the buggy
      // `console.log(data)` line.
      saveLog({
        getUserLogin,
        namespace: 'change_login',
        oldStatus: oldEmail,
        newStatus: newEmail,
      })
    },
  })
}
